import { DEFAULT_PATH_PREVIEW_MODE } from '../Components/PathBuilder/pathBuilderTypes';
import { Project } from '../Components/Projects/projects';
import { MAIN_FILE } from '../fs/fs';
import { ApplicationActions, DEFAULT_PROGRAM_CODE } from './constants';
import { createDefaultFieldSelection, normalizeFieldSelection } from './fieldSelection';
import { checkForUnsupportedCharacters } from './helpers';
import { applyProjectVersionDefaults, cloneProjectVersion, pickProjectVersionFields } from './projectVersions';
import checkProjectName from './validation/checkProjectName';

const { DRIVEBASE_DATA, ACTIONS } = ApplicationActions.Tabs;
const DEFAULT_EDITOR_DRAWER_TAB = 'config-configure';

/**
 * Creates a duplicate of a project with a new ID and local access level
 *
 * This utility consolidates duplicate logic from:
 * - ProjectCard.tsx (handleDuplicate)
 * - ProjectControls.tsx (handleDuplicateSelected)
 * - PrimaryBar.tsx (handleCopyAndEdit)
 *
 * @param project - The project to duplicate
 * @param copyName - Optional custom name for the copy (e.g., translated "Copy of...")
 * @returns A new project object with duplicated data but local access
 */
export function duplicateProject(project: Project, copyName?: string): Project {
  // Generate the copy name
  let checkedProjectName = copyName || `Copy of ${project.name}`;
  checkedProjectName = checkProjectName(checkedProjectName, project.id);

  const sourceVersions = pickProjectVersionFields(project);

  // Create the duplicate project with new ID and local access
  const duplicateProject: Project = applyProjectVersionDefaults({
    program: project?.program || { code: DEFAULT_PROGRAM_CODE, id: undefined },
    name: checkedProjectName,
    file: project?.file || MAIN_FILE,
    id: self.crypto.randomUUID(),
    robotTemplate: project?.robotTemplate || null,
    paths: project?.paths || [],
    pathBuilderFieldRotation: project?.pathBuilderFieldRotation ?? 0,
    selectedField: project?.selectedField ? normalizeFieldSelection(project.selectedField) : createDefaultFieldSelection(),
    selectedPathId: project?.selectedPathId || '',
    pathPreviewMode: project?.pathPreviewMode ?? DEFAULT_PATH_PREVIEW_MODE,
    commandManagementEnabled: project?.commandManagementEnabled ?? true,
    editorOpenDrawer: project?.editorOpenDrawer ?? true,
    // Clear collaboration fields - duplicate is always local
    shareCode: undefined,
    pin: undefined,
    accessLevel: 'local',
    // Reset collaboration state
    isCollaborative: false,
    collaborators: undefined,
    // Keep other project settings
    lastModified: new Date().toISOString(),
    readOnly: false,
    dashboardLayout: project?.dashboardLayout || [],
    selectedTabs: project?.selectedTabs || { editorTab: ACTIONS, dashboardTab: DRIVEBASE_DATA },
    openDrawer: project?.openDrawer ?? null,
    selectedEditorDrawerTab: project?.selectedEditorDrawerTab ?? DEFAULT_EDITOR_DRAWER_TAB,
    ...(sourceVersions.version && { version: cloneProjectVersion(sourceVersions.version) }),
    ...(sourceVersions.frameworkVersion && { frameworkVersion: cloneProjectVersion(sourceVersions.frameworkVersion) }),
  }) as Project;

  // Update filename to match new project name
  const newFilename = checkForUnsupportedCharacters(duplicateProject.name);
  duplicateProject.file = `${newFilename}.py`;

  return duplicateProject;
}
